import { useState, useCallback } from 'react';
import { UserPlus, ShoppingBag } from 'lucide-react';
import { WizardContainer } from '../WizardContainer';
import { WizardStep } from '../WizardStep';
import { WizardSuccess } from '../WizardSuccess';
import { ContactInfoStep, type WalkInSource } from './ContactInfoStep';
import { AppointmentTypeStep, type WalkInPurpose } from './AppointmentTypeStep';
import { ConfirmStep } from './ConfirmStep';
import { SaleDetailsStep } from '../WalkInSaleWizard/SaleDetailsStep';
import { WalkInSaleConfirmStep } from '../WalkInSaleWizard/WalkInSaleConfirmStep';
import { useWizardState } from '@/hooks/useWizardState';
import { trpc } from '@/trpc/client';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import type { CalendarCategory } from '@/lib/constants';

export interface WalkInWizardProps {
  locationId: string;
  locationName?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (contactId: string, appointmentId: string) => void;
  /** Pre-selected existing contact — skips step 0 */
  existingContact?: { id: string; firstName: string; lastName: string; phone: string; email: string };
}

interface WalkInWizardData {
  // Contact
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  /** How the contact came in — staff selects on the contact-info step.
   *  Defaults to 'walkin'; can be overridden to 'website' or 'referral'. */
  source: WalkInSource;
  // Purpose
  purpose: WalkInPurpose | null;
  // Service/Abholung specific
  notes: string;
  // Verkauf specific
  paymentType: 'leasing' | 'direct' | null;
  productDescription: string;
  deliveryDate: string;
  deliveryDateKnown: boolean;
  /** Who actually closed the sale. Drives revenue attribution on the
   *  Filialleiter dashboard. Defaults to the logged-in user on first
   *  open; staff can override for sales entered on a colleague's behalf. */
  salespersonUserId: string;
}

// Service/Abholung: contact(0) → purpose(1) → confirm(2) → [success]
// Verkauf:          contact(0) → purpose(1) → sale details(2) → sale confirm(3) → [nps] → [success]
const MAX_STEPS = 4;

export function WalkInWizard({
  locationId,
  locationName,
  isOpen,
  onClose,
  onSuccess,
  existingContact,
}: WalkInWizardProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdContactId, setCreatedContactId] = useState<string | null>(existingContact?.id ?? null);
  const [createdAppointmentId, setCreatedAppointmentId] = useState<string | null>(null);
  const [selectedExistingContact, setSelectedExistingContact] = useState<WalkInWizardProps['existingContact'] | null>(existingContact ?? null);

  const {
    step,
    data,
    direction,
    nextStep,
    prevStep,
    updateData,
    reset,
  } = useWizardState<WalkInWizardData>({
    wizardId: `walkin_${locationId}`,
    initialData: {
      firstName: existingContact?.firstName ?? '',
      lastName: existingContact?.lastName ?? '',
      phone: existingContact?.phone ?? '',
      email: existingContact?.email ?? '',
      source: 'walkin',
      purpose: null,
      notes: '',
      paymentType: null,
      productDescription: '',
      deliveryDate: '',
      deliveryDateKnown: true,
      // Default the salesperson to the logged-in user. Staff can change
      // this in the wizard when recording a sale on a colleague's behalf.
      salespersonUserId: user?.id ?? '',
    },
    totalSteps: MAX_STEPS,
    initialStep: existingContact ? 1 : 0,
  });

  const utils = trpc.useUtils();

  // Service/Abholung mutations
  const createContactMutation = trpc.contact.create.useMutation();
  const createAppointmentMutation = trpc.appointment.create.useMutation();

  // Verkauf mutation
  const recordSaleMutation = trpc.appointment.recordWalkInSale.useMutation();

  const isVerkauf = data.purpose === 'verkauf';
  const visibleSteps = isVerkauf ? 4 : 3;

  const handleClose = useCallback(() => {
    reset();
    setShowSuccess(false);
    setCreatedContactId(existingContact?.id ?? null);
    setCreatedAppointmentId(null);
    setSelectedExistingContact(existingContact ?? null);
    onClose();
  }, [reset, onClose, existingContact]);

  const handleContactInfoSubmit = useCallback((contactData: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    source: WalkInSource;
  }) => {
    updateData(contactData);
    setSelectedExistingContact(null);
    nextStep();
  }, [updateData, nextStep]);

  const handleSelectExistingContact = useCallback((contact: { id: string; firstName: string; lastName: string; phone: string; email: string }) => {
    setSelectedExistingContact(contact);
    setCreatedContactId(contact.id);
    updateData({
      firstName: contact.firstName,
      lastName: contact.lastName,
      phone: contact.phone,
      email: contact.email,
    });
    nextStep();
  }, [updateData, nextStep]);

  const handlePurposeSelect = useCallback((purpose: WalkInPurpose) => {
    updateData({ purpose });
    nextStep();
  }, [updateData, nextStep]);

  // --- Service/Abholung confirm ---
  const handleServiceConfirm = useCallback(async (notes: string) => {
    updateData({ notes });

    try {
      let contactId: string;

      if (selectedExistingContact) {
        contactId = selectedExistingContact.id;
      } else {
        const contact = await createContactMutation.mutateAsync({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          email: data.email,
          locationId,
          source: data.source,
        });
        contactId = contact.id;
      }

      setCreatedContactId(contactId);

      const scheduledAt = new Date(Date.now() + 60_000);
      const appointment = await createAppointmentMutation.mutateAsync({
        contactId,
        category: data.purpose as Exclude<CalendarCategory, 'internal_block'>,
        type: 'in_person',
        scheduledAt,
        durationMinutes: 30,
        notes: notes || undefined,
      });

      setCreatedAppointmentId(appointment.id);

      utils.contact.list.invalidate();
      utils.appointment.list.invalidate();

      setShowSuccess(true);
    } catch (error) {
      console.error('Failed to create walk-in:', error);
      const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
      toast.error({
        title: 'Fehler beim Erstellen',
        message,
      });
    }
  }, [data, locationId, createContactMutation, createAppointmentMutation, updateData, utils, toast]);

  // --- Verkauf: sale details submit ---
  const handleSaleDetailsSubmit = useCallback((saleData: {
    paymentType: 'leasing' | 'direct';
    productDescription: string;
    deliveryDate: string;
    deliveryDateKnown: boolean;
    notes: string;
    salespersonUserId: string;
  }) => {
    updateData(saleData);
    nextStep();
  }, [updateData, nextStep]);

  // --- Verkauf: sale confirm ---
  const handleSaleConfirm = useCallback(() => {
    if (!data.paymentType) return;

    recordSaleMutation.mutate({
      locationId,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      email: data.email,
      source: data.source,
      paymentType: data.paymentType,
      productDescription: data.productDescription || undefined,
      deliveryDate: data.deliveryDateKnown && data.deliveryDate
        ? new Date(data.deliveryDate)
        : undefined,
      notes: data.notes || undefined,
      // Only forward when the user explicitly picked someone — the server
      // falls back to ctx.user when omitted.
      salespersonUserId: data.salespersonUserId || undefined,
    }, {
      onSuccess: (result: { contactId: string; appointmentId: string; contactCreated: boolean }) => {
        setCreatedContactId(result.contactId);
        setCreatedAppointmentId(result.appointmentId);

        toast.success({
          title: 'Verkauf erfasst',
          message: `${data.firstName} ${data.lastName} — ${data.paymentType === 'leasing' ? 'Leasing' : 'Kauf'}`,
        });

        utils.contact.list.invalidate();
        utils.appointment.list.invalidate();

        setShowSuccess(true);
      },
      onError: (error: { message: string }) => {
        toast.error({
          title: 'Fehler beim Erfassen',
          message: error.message,
        });
      },
    });
  }, [data, locationId, recordSaleMutation, utils, toast]);

  const handleSuccessDone = useCallback(() => {
    if (createdContactId && createdAppointmentId) {
      onSuccess(createdContactId, createdAppointmentId);
    }
    handleClose();
  }, [createdContactId, createdAppointmentId, onSuccess, handleClose]);

  const isLoading = createContactMutation.isPending || createAppointmentMutation.isPending || recordSaleMutation.isPending;

  if (!isOpen) return null;

  // NPS step (verkauf only, after sale is recorded)
  // Success screen
  if (showSuccess) {
    const successMessage = isVerkauf
      ? `${data.firstName} ${data.lastName} wurde als Kunde erfasst.${data.paymentType === 'leasing' ? ' Die Leasing-Nachverfolgung wurde gestartet.' : ''}`
      : `${data.firstName} ${data.lastName} wurde als Kunde angelegt und ein Sofort-Termin wurde erstellt.`;

    return (
      <WizardContainer
        isOpen={isOpen}
        onClose={handleClose}
        title={isVerkauf ? 'Walk-In Verkauf' : 'Walk-In Kunde'}
        icon={isVerkauf
          ? <ShoppingBag className="w-5 h-5 text-green-400" />
          : <UserPlus className="w-6 h-6 text-white" />
        }
        iconBgColor={isVerkauf ? undefined : 'bg-white/20'}
        headerGradient={isVerkauf ? undefined : 'from-purple-600 to-purple-700'}
        currentStep={visibleSteps}
        totalSteps={visibleSteps}
      >
        <WizardSuccess
          title={isVerkauf ? 'Verkauf erfasst!' : 'Walk-In erfasst!'}
          message={successMessage}
          onDone={handleSuccessDone}
        />
      </WizardContainer>
    );
  }

  return (
    <WizardContainer
      isOpen={isOpen}
      onClose={handleClose}
      title="Walk-In Kunde"
      subtitle={isVerkauf ? locationName : 'Neuer Kunde ohne Termin'}
      icon={<UserPlus className="w-6 h-6 text-white" />}
      iconBgColor="bg-white/20"
      headerGradient="from-purple-600 to-purple-700"
      totalSteps={visibleSteps}
      currentStep={step}
      showBack={step > 0 && !showSuccess}
      onBack={prevStep}
    >
      {/* Step 0: Contact Info */}
      <WizardStep isActive={step === 0} direction={direction}>
        <ContactInfoStep
          initialData={{
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            email: data.email,
            source: data.source,
          }}
          onSubmit={handleContactInfoSubmit}
          onSelectExisting={handleSelectExistingContact}
        />
      </WizardStep>

      {/* Step 1: Purpose Selection */}
      <WizardStep isActive={step === 1} direction={direction}>
        <AppointmentTypeStep onSelect={handlePurposeSelect} />
      </WizardStep>

      {/* Step 2 (Service/Abholung): Confirm */}
      {!isVerkauf && (
        <WizardStep isActive={step === 2} direction={direction}>
          <ConfirmStep
            contact={{
              firstName: data.firstName,
              lastName: data.lastName,
              phone: data.phone,
              email: data.email,
            }}
            category={data.purpose as Exclude<CalendarCategory, 'internal_block'>}
            locationName={locationName}
            onConfirm={handleServiceConfirm}
            isLoading={isLoading}
          />
        </WizardStep>
      )}

      {/* Step 2 (Verkauf): Sale Details */}
      {isVerkauf && (
        <WizardStep isActive={step === 2} direction={direction}>
          <SaleDetailsStep
            locationId={locationId}
            initialData={{
              paymentType: data.paymentType,
              productDescription: data.productDescription,
              deliveryDate: data.deliveryDate,
              deliveryDateKnown: data.deliveryDateKnown,
              notes: data.notes,
              salespersonUserId: data.salespersonUserId,
            }}
            onSubmit={handleSaleDetailsSubmit}
            onBack={prevStep}
          />
        </WizardStep>
      )}

      {/* Step 3 (Verkauf): Sale Confirm */}
      {isVerkauf && (
        <WizardStep isActive={step === 3} direction={direction}>
          <WalkInSaleConfirmStep
            data={{
              firstName: data.firstName,
              lastName: data.lastName,
              phone: data.phone,
              email: data.email,
              paymentType: data.paymentType as 'leasing' | 'direct',
              productDescription: data.productDescription,
              deliveryDate: data.deliveryDate,
              deliveryDateKnown: data.deliveryDateKnown,
              notes: data.notes,
            }}
            onConfirm={handleSaleConfirm}
            onBack={prevStep}
            isSubmitting={recordSaleMutation.isPending}
          />
        </WizardStep>
      )}
    </WizardContainer>
  );
}
